"use client";

import Link from "next/link";
import type { PublicEvent, RsvpStatus } from "./EventTypes";

type TicketOption = {
  id: string;
  label: string;
  price: string | null;
  originalPrice: string | null;
  description: string | null;
  quantityTotal: number | null;
  quantitySold: number | null;
  startsAt: string | null;
  expiresAt: string | null;
  tierStatus: "active" | "upcoming" | "expired" | "sold_out";
};

export type BottomActionBarProps = {
  event: PublicEvent;
  rsvpState: "idle" | "loading" | "confirmed" | "approved" | "pending" | "waitlist" | "rejected" | "cancelled" | "error";
  rsvpStatus: RsvpStatus;
  rsvpError: string | null;
  isPaidEvent: boolean;
  isFreeEvent: boolean;
  isEnded: boolean;
  isCancelled: boolean;
  isRestricted: boolean;
  hasTicketTiers: boolean;
  needsRequest: boolean;
  canRsvp: boolean;
  startingFromPriceLabel: string;
  priceLabel: string;
  primaryCtaLabel: string;
  isModalOpen: boolean;
  failedPaymentInfo: { orderId: string; paymentId: string; signature: string } | null;
  retryingVerification: boolean;
  onRsvp: () => void;
  onOpenTicketPicker: () => void;
  onCancelConfirm: () => void;
  onRetryVerification: () => void;
};

export function BottomActionBar({
  event,
  rsvpState,
  rsvpError,
  isPaidEvent,
  isFreeEvent,
  isEnded,
  isCancelled,
  hasTicketTiers,
  needsRequest,
  canRsvp,
  startingFromPriceLabel,
  primaryCtaLabel,
  isModalOpen,
  failedPaymentInfo,
  retryingVerification,
  onRsvp,
  onOpenTicketPicker,
  onCancelConfirm,
  onRetryVerification,
}: BottomActionBarProps) {
  return (
    <div className={`fixed bottom-0 inset-x-0 z-[60] bg-[rgba(5,5,8,0.88)] backdrop-blur-[24px] border-t border-white/[0.08] px-7 pt-3.5 pb-[calc(0.875rem+env(safe-area-inset-bottom))] transition-opacity duration-150 ${isModalOpen ? "opacity-0 pointer-events-none" : "opacity-100"}`}>
      <div className="max-w-[1200px] mx-auto flex items-center justify-between">
        <div>
          <p className="text-[11px] text-white/50">Starting from {!isFreeEvent && <span className="text-white/45">+ platform fee</span>}</p>
          <p className="font-semibold text-xl text-white">{startingFromPriceLabel}</p>
          {rsvpState === "rejected" && !isEnded && !isCancelled ? (
            <p className="text-[11px] text-red-400/80 mt-0.5">Your previous request was not approved</p>
          ) : needsRequest && !isEnded && !isCancelled ? (
            <p className="text-[11px] text-white/50 mt-0.5">Approval required</p>
          ) : isPaidEvent ? (
            <Link href="/refund" className="text-[11px] text-white/50 hover:text-white/50 transition-colors mt-0.5 inline-block">
              Refund Policy
            </Link>
          ) : null}
        </div>
        <button
          onClick={
            rsvpState === "approved" || (rsvpState === "confirmed" && isPaidEvent)
              ? () => onOpenTicketPicker()
              : isPaidEvent && hasTicketTiers && !needsRequest
                ? () => onOpenTicketPicker()
                : onRsvp
          }
          disabled={
            !canRsvp ||
            rsvpState === "loading" ||
            (rsvpState === "confirmed" && !isPaidEvent) ||
            rsvpState === "pending" ||
            rsvpState === "waitlist"
          }
          className="px-6 sm:px-10 py-3 min-h-[44px] rounded-full bg-[var(--hopp-gold)] text-white font-semibold text-[13px] sm:text-sm whitespace-nowrap tracking-[0.03em] shadow-[0_0_32px_rgba(212,175,55,0.18)] hover:opacity-90 active:scale-[0.98] transition disabled:opacity-50 disabled:shadow-none"
        >
          {isEnded
            ? "Event ended"
            : isCancelled
              ? "Event cancelled"
              : rsvpState === "confirmed" && isPaidEvent
                ? "Buy tickets"
                : rsvpState === "confirmed"
                  ? "You're going"
                  : rsvpState === "approved"
                    ? (isPaidEvent ? "Pay now" : "Get tickets")
                    : rsvpState === "pending"
                      ? "Pending"
                      : rsvpState === "waitlist"
                        ? "Waitlisted"
                        : rsvpState === "rejected"
                          ? "Request again"
                          : rsvpState === "cancelled"
                            ? "RSVP canceled"
                            : rsvpState === "loading"
                              ? "Saving..."
                              : primaryCtaLabel}
        </button>
        {(rsvpState === "confirmed" || rsvpState === "approved") && !isEnded && !isCancelled && (
          <button
            onClick={() => onCancelConfirm()}
            className="text-[11px] text-white/45 hover:text-red-400 transition-colors ml-2"
          >
            Cancel
          </button>
        )}
      </div>
      {rsvpState === "error" && rsvpError ? (
        <div className="max-w-[1200px] mx-auto mt-1">
          <p className="text-xs text-red-400">{rsvpError}</p>
          {failedPaymentInfo && (
            <div className="flex items-center gap-3 mt-2">
              <button
                onClick={onRetryVerification}
                disabled={retryingVerification}
                className="text-xs font-medium text-white/70 hover:text-white underline disabled:opacity-50"
              >
                {retryingVerification ? "Retrying..." : "Retry Verification"}
              </button>
              <a
                href={`mailto:info@hopp.life?subject=Payment%20Issue&body=Order%20ID%3A%20${failedPaymentInfo.orderId}%0APayment%20ID%3A%20${failedPaymentInfo.paymentId}%0AEvent%3A%20${encodeURIComponent(event?.title ?? "")}`}
                className="text-xs font-medium text-white/50 hover:text-white/70 underline"
              >
                Contact Support
              </a>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
